import { ConfiguratorUiModule } from '@icc/configurator/ui';
import { NgModule, Injector, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { registerWindowDoorConfigurations, ComponentsServiceBase } from '@icc/window-door/types';
import { ComponentsService } from './door/components/components.service';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import {
    WINDOW_DOOR_FEATURE_KEY,
    initialState as windowDoorInitialState,
    windowDoorReducer,
} from './+state/window-door.reducer';
import { WindowDoorEffects } from './+state/window-door.effects';
import { WindowDoorFacade } from './+state/window-door.facade';
import { SharedModule } from '@icc/configurator/shared';
import { AccessoriesService, ConstructionElementsConfiguratorModule } from '@icc/construction-elements/configurator';

// Accessory state management (from construction-elements)
import {
    ACCESSORY_FEATURE_KEY,
    initialState as accessoryInitialState,
    accessoryReducer,
} from './+state/accessory.reducer';
import { AccessoryEffects } from './+state/accessory.effects';
import { WindowAccessoryFacade } from './+state/accessory.facade';
import { WindowAccessoriesComponent } from './accessories/accessories.component';
import { WindowAccessoriesService } from './accessories/accessories.service';
import { AccessoriesSpaceService } from './accessories/accessories-space.service';
import { WindowAccessoriesListPageComponent } from './accessories-list-page/accessories-list-page.component';
import { AlignmentsService } from './layout/alignments.service';
import { ExtensionsService } from './layout/extensions.service';
import { ThresholdsService } from './layout/thresholds.service';
import { SillsListPageComponent } from './sills-list-page/sills-list-page.component';
import { SillOptionsPageComponent } from './sill-options-page/sill-options-page.component';
import { CassonettoOptionsPageComponent } from './cassonetto-options-page/cassonetto-options-page.component';
import { CassonettosListPageComponent } from './cassonettos-list-page/cassonettos-list-page.component';
import { ComplementaryGoodsService } from './complementary-goods.service';
import { WindowSillsService } from './window-sills.service';
import { GlassTypeVariantsPageComponent } from './glass-type-variants-page/glass-type-variants-page.component';
import { FillingsFiltersPageComponent } from './fillings-filters-page/fillings-filters-page.component';

// Window components
import { DimensionsComponent } from './dimensions/dimensions.component';
import { SystemComponent } from './system/system.component';
import { ShapesComponent } from './shapes/shapes.component';
import { LayoutComponent } from './layout/layout.component';
import { GlazingsComponent } from './glazings/glazings.component';
import { MuntinsComponent } from './muntins/muntins.component';
import { FittingComponent } from './fitting/fitting.component';
import { MuntinsLayoutsComponent } from './muntins-layouts/muntins-layouts.component';
import { MuntinsColorsComponent } from './muntins-colors/muntins-colors.component';
import { ChangeFittingPageComponent } from './change-fitting-page/change-fitting-page.component';
import { DimensionsModule } from './modules/dimensions/dimensions.module';
import { WindowParametersService } from './parameters/window-parameters.service';
import { LayoutModule } from './modules/layout/layout.module';
import { PriceModule } from '@icc/price/b2b';
import { WindowDoorPriceModule } from './+price';
import { DecoPanelOptionsPageComponent } from './deco-panel-options-page/deco-panel-options-page.component';
import { LayoutOptionsPageComponent } from './layout-options-page/layout-options-page.component';
import { HandlesPageComponent } from './fittings/handles-page/handles-page.component';
import { HingesPageComponent } from './fittings/hinges-page/hinges-page.component';
import { HandlesModule } from './modules/handles/handles.module';
import { MuntinsModule } from './modules/muntins/muntins.module';
import { MuntinsLayoutEditPageComponent } from './muntins-layout-edit-page/muntins-layout-edit-page.component';
import { GlazingsModule } from './modules/glazings/glazings.module';
import { FillingColorsPageComponent } from './filling-colors-page/filling-colors-page.component';
import { GlazingUnitPageComponent } from './glazing-unit-page/glazing-unit-page.component';
import { GlazingBeadPageComponent } from './glazing-bead-page/glazing-bead-page.component';
import { GlazingSpacerPageComponent } from './glazing-spacer-page/glazing-spacer-page.component';
import { SealColorsPageComponent } from './seal-colors-page/seal-colors-page.component';
import { SiliconeColorsPageComponent } from './silicone-colors-page/silicone-colors-page.component';
import { SashLayoutPageComponent } from './sash-layout-page/sash-layout-page.component';
import { SashTypesPageComponent } from './sash-types-page/sash-types-page.component';
import { MountingMethodPageComponent } from './mounting-method-page/mounting-method-page.component';
import { SystemsModule } from './modules/systems/systems.module';
import { SashesModule } from './modules/sashes/sashes.module';
import { GlazingLayoutPageComponent } from './glazing-layout-page/glazing-layout-page.component';
import { EditHandlesPageComponent } from './edit-handles-page/edit-handles-page.component';
import { AccessoriesLocationPageComponent } from './accessories-location-page/accessories-location-page.component';
import { HandlesOptionsPageComponent } from './handles-options-page/handles-options-page.component';
import { HingesOptionsPageComponent } from './hinges-options-page/hinges-options-page.component';
import { SystemInfoComponent } from './system-info/system-info.component';
import { PanelGlazingsListPageComponent } from './panel-glazings-list-page/panel-glazings-list-page.component';
import { ConstructionComponent } from './construction/construction.component';
import { ReinforcementsModule } from './reinforcements/reinforcements.module';
import { ReinforcementsListPageComponent } from './reinforcements/reinforcements-list-page/reinforcements-list-page.component';
import { ProductDetailsComponent } from './product-details/product-details.component';
import { ConstructionParametersComponent } from './construction-parameters/construction-parameters.component';
import { MeasurementsPageComponent } from './measurements-page/measurements-page.component';
import { DimensionsAdditionalDataPageComponent } from './dimensions-additional-data-page/dimensions-additional-data-page.component';
import { MountingClearanceComponent } from './mounting-clearance/mounting-clearance.component';
import { MuntinsTypesModalComponent } from './muntins-types-modal/muntins-types-modal.component';
import { SystemFiltersComponent } from './system-filters/system-filters.component';
import { SystemPacketsComponent } from './system-packets/system-packets.component';
import { FillingsFacade } from './glazings/fillings.facade';
import { ConfiguratorPagesModule } from '@icc/configurator/pages';
import { PanelOptionsFacade } from './deco-panel-options-page/panel-options.facade';
import { MosquitoComponent } from './mosquito/mosquito.component';
import { SashMosquitoComponent } from './sash-mosquito/sash-mosquito.component';
import { MuntinsInfoComponent } from './muntins-info-component/muntins-info.component';
import { PanelTrimsListPageComponent } from './panel-trims-list-page/panel-trims-list-page.component';
import { WindowProfilesListPageComponent } from './profiles/profiles-list-page/profiles-list-page.component';
import { ProfileOptionsPageComponent } from './profiles/profile-options-page/profile-options-page.component';
import { ProfileSetsPageComponent } from './profiles/profile-sets-page/profile-sets-page.component';
import { ProfilesShapesPageComponent } from './profiles/profiles-shapes-page/profiles-shapes-page.component';
import { ThresholdsColorsComponent } from './profiles/thresholds-colors-page/thresholds-colors.component';
import { FillingOptionsPageComponent } from './filling-options-page/filling-options-page.component';
import { GlassTypesPageComponent } from './glass-types-page/glass-types-page.component';
import { AlushellService } from './materials/alushell.service';
import { ColorsComponent } from './colors/colors.component';
import { FillingsListPageComponent } from './fillings-list-page/fillings-list-page.component';
import { ProfilesModule } from './profiles/profiles.module';

// Door components
import { LocksComponent } from './door/locks/locks.component';
import { ModelComponent } from './door/model/model.component';
import { HardwareComponent } from './door/hardware/hardware.component';
import { HardwareTypesPageComponent } from './door/hardware-types-page/hardware-types-page.component';
import { HardwareTypeOptionsPageComponent } from './door/hardware-type-options-page/hardware-type-options-page.component';
import { PlatesPageComponent } from './door/plates-page/plates-page.component';
import { CylindersPageComponent } from './door/cylinders-page/cylinders-page.component';
import { SizesComponent } from './door/sizes/sizes.component';
import { ExtensionsPageComponent } from './door/extensions-page/extensions-page.component';
import { LightsGlazingComponent } from './door/lights-glazing/lights-glazing.component';
import { ModelListPageComponent } from './door/model-list-page/model-list-page.component';
import { ModelInfoComponent } from './door/model-info/model-info.component';
import { ElectronicLockOptionsPageComponent } from './door/electronic-lock-options-page/electronic-lock-options-page.component';
import { ElectronicLockPageComponent } from './door/electronic-lock-page/electronic-lock-page.component';
import { SlatsComponent } from './door/slats/slats.component';
import { RecessedPullOptionsPageComponent } from './door/recessed-pull-type-options-page/recessed-pull-options-page.component';
import { DependenciesService } from './dependencies/dependencies.service';
import { WindowDoorTimeLimitModule } from './time-limit/window-door-time-limit.module';
import { ModelService } from './door/model/model.service';

export function registerWindowDoorConfigurator() {
    return () => {
        registerWindowDoorConfigurations();
    };
}

@NgModule({
    declarations: [
        // Construction elements components (from construction-elements)
        WindowAccessoriesComponent,
        WindowAccessoriesListPageComponent,
        SillsListPageComponent,
        SillOptionsPageComponent,
        CassonettoOptionsPageComponent,
        CassonettosListPageComponent,
        GlassTypeVariantsPageComponent,
        FillingsFiltersPageComponent,
        FillingsListPageComponent,
        // Window components
        DimensionsComponent,
        SystemComponent,
        ShapesComponent,
        LayoutComponent,
        GlazingsComponent,
        MuntinsComponent,
        FittingComponent,
        MuntinsLayoutsComponent,
        MuntinsColorsComponent,
        ChangeFittingPageComponent,
        DecoPanelOptionsPageComponent,
        LayoutOptionsPageComponent,
        HandlesPageComponent,
        HingesPageComponent,
        MuntinsLayoutEditPageComponent,
        FillingColorsPageComponent,
        FillingOptionsPageComponent,
        GlassTypesPageComponent,
        GlazingUnitPageComponent,
        GlazingBeadPageComponent,
        GlazingSpacerPageComponent,
        SealColorsPageComponent,
        SiliconeColorsPageComponent,
        SashLayoutPageComponent,
        SashTypesPageComponent,
        MountingMethodPageComponent,
        GlazingLayoutPageComponent,
        EditHandlesPageComponent,
        AccessoriesLocationPageComponent,
        HandlesOptionsPageComponent,
        HingesOptionsPageComponent,
        SystemInfoComponent,
        WindowProfilesListPageComponent,
        ProfileOptionsPageComponent,
        ProfileSetsPageComponent,
        ProfilesShapesPageComponent,
        ThresholdsColorsComponent,
        PanelGlazingsListPageComponent,
        PanelTrimsListPageComponent,
        ConstructionComponent,
        ReinforcementsListPageComponent,
        ProductDetailsComponent,
        ConstructionParametersComponent,
        MeasurementsPageComponent,
        DimensionsAdditionalDataPageComponent,
        MountingClearanceComponent,
        MuntinsTypesModalComponent,
        SystemFiltersComponent,
        SystemPacketsComponent,
        MosquitoComponent,
        SashMosquitoComponent,
        MuntinsInfoComponent,
        // Door components
        LocksComponent,
        ModelComponent,
        HardwareComponent,
        HardwareTypesPageComponent,
        HardwareTypeOptionsPageComponent,
        RecessedPullOptionsPageComponent,
        ElectronicLockOptionsPageComponent,
        ElectronicLockPageComponent,
        PlatesPageComponent,
        CylindersPageComponent,
        SizesComponent,
        ExtensionsPageComponent,
        LightsGlazingComponent,
        ModelListPageComponent,
        ModelInfoComponent,
        SlatsComponent,
        ColorsComponent,
    ],
    imports: [
        CommonModule,
        SharedModule,
        ConfiguratorUiModule,
        ConfiguratorPagesModule,
        ConstructionElementsConfiguratorModule,
        StoreModule.forFeature(WINDOW_DOOR_FEATURE_KEY, windowDoorReducer, {
            initialState: windowDoorInitialState,
        }),
        StoreModule.forFeature(ACCESSORY_FEATURE_KEY, accessoryReducer, {
            initialState: accessoryInitialState,
        }),
        EffectsModule.forFeature([WindowDoorEffects, AccessoryEffects]),
        DimensionsModule,
        LayoutModule,
        PriceModule,
        WindowDoorPriceModule,
        HandlesModule,
        MuntinsModule,
        GlazingsModule,
        SystemsModule,
        SashesModule,
        ReinforcementsModule,
        ProfilesModule,
        WindowDoorTimeLimitModule,
    ],
    providers: [
        provideAppInitializer(() => {
            const initializerFn = (registerWindowDoorConfigurator)();
            return initializerFn();
        }),
        WindowDoorFacade,
        WindowParametersService, // Nowy serwis dla window/door parameters
        FillingsFacade,
        PanelOptionsFacade,
        // Construction elements providers
        WindowAccessoryFacade,
        ComplementaryGoodsService,
        WindowSillsService,
        WindowAccessoriesService,
        { provide: AccessoriesService, useExisting: WindowAccessoriesService },
        AccessoriesSpaceService,
        AlignmentsService,
        ExtensionsService,
        ThresholdsService,
        DependenciesService,
        AlushellService,
        ModelService,
        // Rejestracja ComponentsService jako implementacji abstrakcji dla details.component
        ComponentsService,
        { provide: ComponentsServiceBase, useExisting: ComponentsService },
    ],
    exports: [
        // Construction elements exports
        SillsListPageComponent,
        SillOptionsPageComponent,
        CassonettoOptionsPageComponent,
        CassonettosListPageComponent,
        GlassTypeVariantsPageComponent,
        FillingsFiltersPageComponent,
        FillingsListPageComponent,
        // Window exports
        PanelGlazingsListPageComponent,
        MeasurementsPageComponent,
        DimensionsAdditionalDataPageComponent,
        MountingClearanceComponent,
        MuntinsTypesModalComponent,
        SystemFiltersComponent,
        SystemPacketsComponent,
        MosquitoComponent,
        SashMosquitoComponent,
        ColorsComponent,
        // Door exports
        HardwareComponent,
        HardwareTypesPageComponent,
        HardwareTypeOptionsPageComponent,
        RecessedPullOptionsPageComponent,
        ElectronicLockOptionsPageComponent,
        ElectronicLockPageComponent,
        PlatesPageComponent,
        CylindersPageComponent,
        SizesComponent,
        ExtensionsPageComponent,
        LightsGlazingComponent,
        ModelListPageComponent,
        ModelInfoComponent,
    ]
})
export class WindowDoorConfiguratorModule {}
